import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/opt/build/repo/src/ui/layout.js";
import { Link } from 'gatsby';
import { createElement } from 'react';
import rehype2react from 'rehype-react';
import Doc from 'unified-doc';
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <h3 {...{
      "id": "overview"
    }}>{`Overview`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`textContent`}</inlineCode>{` of a document is the concatenated value of text values in all text nodes.  This data is used by many API methods (e.g. `}<inlineCode parentName="p">{`doc.search()`}</inlineCode>{`, `}<inlineCode parentName="p">{`doc.file('.txt')`}</inlineCode>{`, `}<inlineCode parentName="p">{`marks`}</inlineCode>{`).  It can also be useful in a couple of scenarios outside of `}<inlineCode parentName="p">{`unified-doc`}</inlineCode>{` to compute derived data that can be passed back to `}<inlineCode parentName="p">{`unified-doc`}</inlineCode>{` as `}<inlineCode parentName="p">{`marks`}</inlineCode>{`.`}</p>
    <h3 {...{
      "id": "use-outside-unified-doc"
    }}>{`Use outside `}<inlineCode parentName="h3">{`unified-doc`}</inlineCode></h3>
    <p><inlineCode parentName="p">{`doc.textContent`}</inlineCode>{` will easily return you the text content of a document (irregardless of its content type).  You can use this data for anything (e.g. in various NLP pipelines).`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js",
        "metastring": "live=true",
        "live": "true"
      }}>{`// import Doc from 'unified-doc';

function() {
  const term = 'content';
  const content = '> **some** markdown content, content, content';
  const filename = 'doc.md';
  const doc = Doc({ content, filename });
  const textContent = doc.textContent();

  function countPipeline(text, term) {
    return (text.match(new RegExp(\`\${term}\`, 'gi')) || []).length;
  }

  return (
    <div>
      <div>
        <strong>Text content:</strong>{' '}
        <code>{textContent}</code>
      </div>
      <div>
        <strong>Term:</strong>{' '}
        <code>{term}</code>
      </div>
      <div>
        <strong>Count:</strong>{' '}
        <code>{countPipeline(textContent, term)}</code>
      </div>
    </div>
  );
}
`}</code></pre>
    <h3 {...{
      "id": "compute-marks"
    }}>{`Compute `}<inlineCode parentName="h3">{`marks`}</inlineCode></h3>
    <p>{`You can use the `}<inlineCode parentName="p">{`textContent`}</inlineCode>{` to compute `}<inlineCode parentName="p">{`marks`}</inlineCode>{` by calculating `}<inlineCode parentName="p">{`start`}</inlineCode>{` and `}<inlineCode parentName="p">{`end`}</inlineCode>{` offset of matched terms and repipe that data to `}<inlineCode parentName="p">{`unified-doc`}</inlineCode>{` for visually marking nodes.  Note that the `}<inlineCode parentName="p">{`doc.search()`}</inlineCode>{` method (explored more in the `}<Link to="/recipes/search" mdxType="Link">{`Search`}</Link>{` section) should be the preferred way to do this (since search results are compatible interfaces with `}<inlineCode parentName="p">{`mark`}</inlineCode>{` interfaces).`}</p>
    <p>{`This example demonstrates how the `}<inlineCode parentName="p">{`marks`}</inlineCode>{` data could be computed outside of a `}<inlineCode parentName="p">{`doc`}</inlineCode>{` instance (e.g. by a server), and the results will be fully compatible with `}<inlineCode parentName="p">{`unified-doc`}</inlineCode>{` since the offsets are  based on the `}<inlineCode parentName="p">{`textContent`}</inlineCode>{` of a `}<inlineCode parentName="p">{`doc`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js",
        "metastring": "live=true",
        "live": "true"
      }}>{`// import { createElement } from 'react';
// import rehype2react from 'rehype-react';
// import Doc from 'unified-doc';

function() {
  const content = '> **some** markdown content, content, content';
  const filename = 'doc.md';

  // create a temporary doc to extract its text content
  const textContent = Doc({ content, filename }).textContent();

  // a crude example of what a server would do given \`textContent\` and a \`query string\`.  Perhaps the server would deconstruct a more complex query string, fetch some relating data from parsed IDs, check against caches and data stores and finally decide how to return marks.
  function getMarksFromServer(textContent, query) {
    return Array.from(textContent.matchAll(query))
      .map(match => {
        const start = match.index;
        return {
          id: start,
          start,
          end: start + query.length,
        };
      })
  }

  const doc = Doc({
    compiler: [[rehype2react, { createElement }]],
    content: '> **some** markdown content, content, content',
    filename: 'doc.md',
    marks: getMarksFromServer(textContent, 'content'),
  });

  return (
    <div>
      <h4>Compiled</h4>
      {doc.compile().result}
    </div>
  );
}
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      